// The Deals desk (module 10 U151; the brief DEALS-PAGE-BRIEF-2026-09-22.md section 4; drawn in
// screens/2026-09-22-deals-concepts/concept-a-desk.html).
//
// Top to bottom: the head with a find field, the show strip while a show is ahead, "Cobrain heard
// these", "Yours" in three groups by who moves next, "Not allocated, from HubSpot" folded to one line
// (U156), "The team's" as full cards, and "Signed and closed" folded at the foot. The order and the
// three changes to it are Mike's, by pop-up in Cowork on 2026-09-24 (memory/steering/portal/
// DEALS-CONTRACT.md, "The layout"; REQ-2026-09-24-45).
//
// U152 IS THE RULE THIS FILE IS WRITTEN TO, in Mike's words: "all areas where data is displayed is
// mapped to real data". So:
//   - every value drawn below comes from the desk read, and there is no constant, sample row or
//     seeded demo text anywhere in this file;
//   - a band whose source is not built yet is NOT DRAWN AT ALL rather than drawn empty. "Cobrain
//     heard these" is drawn only while a proposal waits: track `dealsdata`'s detection pass writes
//     crm.deal_proposal and is switched on for nobody yet. The show strip is track `show`'s ShowStrip,
//     which draws itself only while a show with a booked meeting is ahead (U158);
//   - where a source exists and is empty, the band IS drawn and says in words what was read.
// Measured on the live store on the day this was built: 191 deals, all 191 from HubSpot, all 191
// Mike's, 0 opened from a conversation. So on live this page is honestly almost all empty text, and
// that is the finding rather than a reason to pad it.
import { ActionInput, ActionButton, CompanyName } from '../parts/index.js';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { formatDate } from '../labels.js';
import { Loading, useLoad, type PageProps } from '../records/shared.js';
import { records, triage, Unauthenticated } from '../api.js';
import { callSaid, openCallWindow, placeCall, VIDEO_CALL } from '../call/api.js';
import { foundDealWhat } from './heat-words.js';
import { dealsApi, type Found, type Closed, type Desk, type DeskDeal, type HeldCopy, type Heard, type Lead, type LeadOption, type NextStep, type Rollout, type StageStep } from './api.js';
import { stateWords } from './DealPage.js';
import { ShowStrip } from '../show/ShowStrip.js';
import './deals.css';

/** The words for what moved, written here and never by the API, which returns the thing itself. */
export function movedWords(d: DeskDeal): { line: string; source: string | null } | null {
  if (!d.moved) return null;
  const when = formatDate(d.moved.when);
  if (d.moved.kind === 'heard') return { line: d.moved.detail, source: `${sourceWord(d.moved.source)}, ${when}` };
  // "Read as" is Cobrain's reading. A change a person made (user_override: accepting a proposal, a
  // stage set by hand) is theirs, and saying Cobrain read it would take the credit from them.
  const byHand = d.moved.source === 'user_override';
  if (d.moved.kind === 'stage') return { line: `${byHand ? 'Moved to' : 'Read as'} ${d.moved.detail.toLowerCase()}.`, source: when };
  return { line: `Value ${byHand ? 'set at' : 'read as'} ${money(d.moved.detail)}.`, source: when };
}

/**
 * A value as a person writes it. The change row keeps the amount and its currency as one string
 * ("130000.00 EUR"), so the trailing zeroes and the bare digits are cleaned up for the card; a
 * string that is not an amount is printed exactly as it was written rather than guessed at.
 */
export function money(raw: string): string {
  const m = /^(-?\d+(?:\.\d+)?)\s*([A-Z]{3})?$/.exec(raw.trim());
  if (!m) return raw;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return raw;
  const shown = n.toLocaleString('en-GB', { maximumFractionDigits: n % 1 === 0 ? 0 : 2 });
  return m[2] ? `${shown} ${m[2]}` : shown;
}

/** Which half of the business (U148). Only ever drawn from the column; null draws nothing. */
export function sideWord(side: string | null): string | null {
  return side === 'rights' ? 'Rights' : side === 'sales' ? 'Sales' : null;
}

/** A signal's kind in the reader's own words. An unknown kind is named plainly, never as its code. */
/** Where a heard proposal came from, in a sentence: "Heard in a Teams chat", never "Heard in teams". */
export function heardInWords(kind: string | null): string {
  switch (kind) {
    case 'email': return 'an email';
    case 'teams_chat': return 'a Teams chat';
    case 'teams_channel': return 'a Teams channel';
    case 'meeting': return 'a meeting';
    case 'calendar_event': return 'the calendar';
    case 'document': return 'a document';
    case 'note': return 'a note';
    default: return 'what Cobrain read';
  }
}

export function sourceWord(kind: string | null): string {
  switch (kind) {
    case 'email': return 'Email';
    case 'teams_chat': return 'Teams';
    case 'teams_channel': return 'a Teams channel';
    case 'meeting': return 'a meeting';
    case 'calendar_event': return 'the calendar';
    case 'document': return 'a document';
    case 'note': return 'a note';
    default: return 'what Cobrain read';
  }
}

/**
 * The steps of the paper track: every stage the store holds EXCEPT the ones it marks as states.
 * U153 -- parked and not of interest are states beside the steps, not steps -- and the store says
 * which is which through each stage's own lifecycle, so this holds no list of codes and a
 * migration that renames or reorders the steps needs no change here.
 */
export function stepsOf(stages: StageStep[]): StageStep[] {
  return stages.filter((s) => s.lifecycle !== 'paused' && s.lifecycle !== 'declined');
}

/**
 * The paper track, drawn from the steps the store defines and in their order (U153).
 *
 * `at` is NULL for a deal that is at a state rather than a step, and nothing is then lit. The stage
 * a parked or declined deal is sitting at is still on the row -- U153 leaves it there on purpose --
 * so reading `stageCode` alone drew "Talking" done and "Terms" current under a card whose own chip
 * said "Not of interest". The lifecycle is what says whether the track means anything.
 */
function Track({ steps, at }: { steps: StageStep[]; at: string | null }) {
  const here = at === null ? -1 : steps.findIndex((s) => s.code === at);
  return (
    <div className="dl-track" aria-label="Where the paper is">
      {steps.map((s, i) => (
        <span key={s.code} className={here >= 0 && i < here ? 'done' : here === i ? 'now' : undefined} title={s.name}>{s.name}</span>
      ))}
    </div>
  );
}

/**
 * Where the deal is on the paper, or the state it is in instead. U153 keeps the two apart: parked
 * and not of interest are states beside the steps, so a parked deal reads "Parked until 3 Oct" and
 * not the step it happened to be sitting at when somebody parked it.
 */
export function stateOrStep(d: DeskDeal): string {
  if (d.lifecycle === 'paused') return d.pausedUntil ? `Parked until ${formatDate(d.pausedUntil)}` : 'Parked';
  if (d.lifecycle === 'declined') return 'Not of interest';
  return d.stageName;
}

/**
 * The next step open on the deal, in words. U152: "No step open" is this READ's empty text and not
 * a constant -- it is said only when the API came back with nothing open on the matter, and the
 * moment somebody accepts a task the card says whose it is and what it is.
 */
/**
 * A band's count. `more` says the desk read hit its ceiling, and a count is then a FLOOR: printing
 * "400 deals" of a store that holds more is a wrong figure stated as a fact, and the store will
 * pass 400 long before anybody remembers this number is here.
 */
export function countWords(n: number, more: boolean): string {
  const s = n === 1 ? '1 deal' : `${n} deals`;
  return more ? `${s} or more` : s;
}

export function stepWords(step: NextStep | null): { who: string; what: string | null } {
  if (!step) return { who: 'No step open', what: null };
  const first = step.ownerName ? step.ownerName.split(' ')[0]! : null;
  const who = step.mine ? 'Next: yours' : first ? `Next: ${first}\u2019s` : 'Next: nobody has it';
  return { who, what: step.title };
}

/**
 * Whose the deal is, said as fact (U53), and never a guess when nobody holds it.
 *
 * It asks WHO HOLDS IT, which is not the question `mine` answers: `mine` is true of a deal the
 * reader is only ON (U145), and reading it here said "You hold it" of a deal that is Will's. The
 * card sits in the Yours band either way; the line inside it still has to be true.
 */
export function holderWords(d: DeskDeal, me: string): string {
  if (d.holderPrincipalId === me) return 'You hold it';
  if (d.holderName) return `${d.holderName} holds it`;
  return 'Nobody holds it';
}

/**
 * How long a deal can be parked for, from the desk. Longer than triage's presets (later today to
 * next week) because a deal is parked for a season, not an afternoon: "they come back after the
 * summer" is the case. A date of the reader's own is always there too.
 */
export function dealParkPresets(now: Date = new Date()): Array<{ key: string; label: string; until: Date }> {
  const at = (days: number): Date => { const d = new Date(now); d.setDate(d.getDate() + days); d.setHours(8, 0, 0, 0); return d; };
  return [
    { key: 'two_weeks', label: 'Two weeks', until: at(14) },
    { key: 'month', label: 'A month', until: at(30) },
    { key: 'quarter', label: 'Three months', until: at(91) },
  ];
}

/**
 * What pressing an option does from the desk. The desk has no draft panel, so an option that writes
 * a draft or opens something goes to the item's own page, where the same option is waiting with the
 * panel beside it; everything else is decided here, on the same pass item and through the same call
 * as the triage card, so it teaches the ranking exactly as it would there (D70).
 */
export function leadGoes(o: LeadOption): 'page' | 'company' | 'decide' {
  if (o.kind === 'open') return typeof o.params?.organisationId === 'string' && o.params.organisationId ? 'company' : 'page';
  if (o.kind === 'draft' || o.kind === 'reply' || o.kind === 'chase') return 'page';
  return 'decide';
}

/** Why the deal is in Yours, said as fact from the read (U145): held, or on it. */
export function yoursWords(d: DeskDeal, me: string): string {
  if (d.holderPrincipalId === me) return 'It is in Yours because you hold it.';
  if (d.mine) return d.holderName ? `It is in Yours because you are on it; ${d.holderName} holds it.` : 'It is in Yours because you are on it.';
  return d.holderName ? `${d.holderName} holds it.` : 'Nobody holds it yet.';
}

/** Where the pass put the item, in words: the reader's list is the first ten (D254 allows up to twenty). */
export function placeWords(l: Lead): string {
  return l.tier === 1 ? `Number ${l.rankNo} on your list` : 'Below the top of your list';
}

/** The roll-out's head line (U154): offered and signed, counted from crm.offer. */
export function rolloutWords(r: Rollout): string {
  const offered = r.offered === 1 ? '1 operator offered' : `${r.offered} operators offered`;
  return `${offered}, ${r.signed} signed, of ${r.total} on the list`;
}

/**
 * Who moves next on a deal in Yours, read from the card's own next step and nothing else (the Deals contract, round 3:
 * "By who moves next (Recommended)", whose option read "Three groups from the card's next step: your move, a
 * colleague's move, no step open. Uses what the card reads now."). A step always has an owner (work.task and
 * work.commitment hold owner_principal_id NOT NULL), so a step that is not the reader's is a colleague's. No step is
 * "none", and nothing else fills it: not a heard date, a stage or a suggested task (B1569).
 */
export type NextMove = 'yours' | 'colleague' | 'none';
export function nextMove(d: DeskDeal): NextMove {
  if (!d.nextStep) return 'none';
  return d.nextStep.mine ? 'yours' : 'colleague';
}

/** The three groups of Yours, in the order the option named them, with the line each says when it holds nothing. */
export const MOVE_GROUPS: ReadonlyArray<{ key: NextMove; head: string; empty: string }> = [
  { key: 'yours', head: 'Your move', empty: 'No deal in Yours has a step open that is yours.' },
  { key: 'colleague', head: 'A colleague’s move', empty: 'No deal in Yours has a step open that a colleague holds.' },
  { key: 'none', head: 'No step open', empty: 'Every deal in Yours has a step open.' },
];

/** Yours split by who moves next, each group keeping the desk read's own order. */
export function byNextMove(ds: DeskDeal[]): Record<NextMove, DeskDeal[]> {
  const out: Record<NextMove, DeskDeal[]> = { yours: [], colleague: [], none: [] };
  for (const d of ds) out[nextMove(d)].push(d);
  return out;
}

/**
 * The folded line of Not allocated (the Deals contract, round 3: "Folded, above the team's (Recommended)", whose option
 * read "One line, '107 not allocated, from HubSpot', sits between Yours and the team's deals. One press opens the
 * rows."). The number is the read's own count, never a written one, and a floor when the read hit its ceiling.
 */
export function unallocatedWords(n: number, more: boolean): string {
  return `${n}${more ? ' or more' : ''} not allocated, from HubSpot`;
}

/** The three presses on a deal nobody holds (U156; Mike by pop-up 2026-09-24, REQ-2026-09-24-40). */
export type AllocateKind = 'take' | 'give' | 'remove';
type Person = Desk['giveTo'][number];
/** What the page does with a press on a deal nobody holds: resolves to the refusal's words, or null when it went through. */
type AllocateFn = (d: DeskDeal, kind: AllocateKind, to?: Person) => Promise<string | null>;

/** What the strip says after a press: the deal and where it went, from the press itself and never the API's words. */
export function allocatedWords(title: string, kind: AllocateKind, to: string | null = null): string {
  if (kind === 'take') return `Taken: ${title} is yours.`;
  if (kind === 'give') return `Given to ${to ?? 'a colleague'}: ${title}.`;
  return `Removed: ${title}. Nothing is deleted, and who removed it and when are kept.`;
}

/** What the strip says once a press is undone: the deal is nobody's again. */
export function undoneWords(title: string): string {
  return `Undone: ${title} is back in Not allocated.`;
}

/** How long the strip stays: triage's hold (U34), and held while the pointer or the keyboard is on it. */
export const ALLOCATE_STRIP_MS = 8_000;

/**
 * "Take it", "Give it to someone" and "Remove" on a deal nobody holds (U156). Take and Remove act at once and the strip
 * at the foot of the page says what happened, with Undo (D62); Give opens the team to pick from. In a team row
 * (`row`) the three are a cell and the picker and any refusal run under the whole row; on a card they sit in its foot.
 */
function AllocateOpts({ d, me, giveTo, allocate, row = false }: { d: DeskDeal; me: string; giveTo: Person[]; allocate: AllocateFn; row?: boolean }) {
  const others = giveTo.filter((t) => t.principalId !== me);
  const [busy, setBusy] = useState(false);
  const [giving, setGiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const press = async (kind: AllocateKind, to?: Person) => {
    setBusy(true); setError(null);
    const refused = await allocate(d, kind, to);
    setBusy(false);
    if (refused) setError(refused); else setGiving(false);
  };
  const buttons = (
    <div className="dl-opts">
      <ActionButton type="button" className="dl-opt" disabled={busy} onClick={() => void press('take')}>Take it</ActionButton>
      <ActionButton type="button" className="dl-opt" disabled={busy} aria-expanded={giving} onClick={() => { setError(null); setGiving(!giving); }}>Give it to someone</ActionButton>
      <ActionButton type="button" className="dl-opt" disabled={busy} onClick={() => void press('remove')}>Remove</ActionButton>
    </div>
  );
  const picker = giving
    ? (
      <div className="dl-panel dl-wide" role={row ? 'cell' : undefined}>
        {others.length > 0
          ? (
            <>
              <p className="dl-reason">Who should hold it?</p>
              <div className="dl-opts">
                {others.map((t) => (
                  <ActionButton key={t.principalId} type="button" className="dl-opt" disabled={busy} onClick={() => void press('give', t)}>{t.name}</ActionButton>
                ))}
                <ActionButton type="button" className="dl-opt" onClick={() => setGiving(false)}>Keep it</ActionButton>
              </div>
            </>
          )
          : <p className="dl-reason">There is nobody else on the team in this store to give it to.</p>}
      </div>
    )
    : null;
  const refused = error ? <p className="dl-err dl-wide" role="alert">{error}</p> : null;
  return row
    ? <><span className="c6" role="cell">{buttons}</span>{picker}{refused}</>
    : <div className="dl-alloc">{buttons}{picker}{refused}</div>;
}

type Panel = 'none' | 'park' | 'why';

function DealCard({ d, steps, me, store, open, onChanged, onSaid, giveTo, allocate }: { d: DeskDeal; steps: StageStep[]; me: string; store: string; open: (p: string) => void; onChanged: () => void; onSaid: (words: string) => void; giveTo: Person[]; allocate: AllocateFn }) {
  const moved = movedWords(d);
  const step = stepWords(d.nextStep);
  const [panel, setPanel] = useState<Panel>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const presets = useMemo(() => dealParkPresets(), []);
  const [until, setUntil] = useState(() => presets[1]!.until.toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const parked = d.lifecycle === 'paused';
  const itemPage = d.lead ? `/items/${encodeURIComponent(d.lead.passItemId)}?store=${encodeURIComponent(store)}` : null;

  // `leaves`: the press takes the card out of the form it is drawn in -- a close folds the deal away, and a deal drawn
  // as a card only for its signed copy goes back to a row -- so its words are said at the top of the page, where they
  // survive the re-read, and not on a card that is about to unmount.
  const act = async (fn: () => Promise<unknown>, words: string, leaves = false) => {
    setBusy(true); setError(null);
    try { await fn(); if (leaves) onSaid(words); else setSaid(words); setPanel('none'); onChanged(); }
    catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(`That did not go through: ${e instanceof Error ? e.message.replace(/\.$/, '') : String(e)}. Nothing was changed.`);
    } finally { setBusy(false); }
  };

  // Actions contract row 3, "Call <name>": placed here as on the card. The tab is taken in the press, since a browser
  // opens one only then, and sent to the meeting when its link comes back. A refusal comes before any meeting is made.
  const call = async (o: LeadOption, video = false) => {
    const tab = openCallWindow();
    setBusy(true); setError(null);
    try {
      const r = await placeCall(d.lead!.passItemId, { store, optionKey: o.key, ...(video ? { video: true } : {}) });
      const words = callSaid(video ? VIDEO_CALL : o.label, r);
      setSaid(tab.go(r.joinUrl) ? words : `${words} Teams gave a link Cobrain does not open, so open the call from your Teams calendar.`);
      setPanel('none'); onChanged();
    } catch (e: unknown) {
      tab.close();
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(`That did not go through: ${e instanceof Error ? e.message.replace(/\.$/, '') : String(e)}. Nothing was changed.`);
    } finally { setBusy(false); }
  };

  const choose = (o: LeadOption) => {
    const goes = leadGoes(o);
    if (goes === 'company') { open(`/companies/${encodeURIComponent(String(o.params!.organisationId))}`); return; }
    if (goes === 'page') { if (itemPage) open(itemPage); return; }
    if (o.kind === 'call') { void call(o); return; }
    void act(() => triage.decide(d.lead!.passItemId, { store, action: 'accept', optionKey: o.key }), `Chosen: ${o.label}.`);
  };

  const park = () => {
    const r = reason.trim();
    if (!r) { setError('Say why it is parked, in a few words.'); return; }
    const when = new Date(`${until}T08:00:00`);
    void act(() => dealsApi.park(store, d.id, when.toISOString(), r), `Parked until ${formatDate(when.toISOString())}.`);
  };

  return (
    <article className="dl-card deal">
      <div className="l">
        <div className="dl-meta">
          {sideWord(d.side) ? <span className="dl-side">{sideWord(d.side)}</span> : null}
          {d.organisationName !== null
            ? <ActionButton reading type="button" className="dotted" onClick={() => open(`/companies/${d.organisationId}`)}>{d.organisationName}</ActionButton>
            : null}
          <span>{holderWords(d, me)}</span>
          {d.onIt.length > 0 ? <span>On it: {d.onIt.join(', ')}</span> : null}
          {/* A deal at a STATE rather than a step says so here, in the same words the team rows
              use. Until this line was added a declined deal drew an unlit track and nothing else,
              so "not of interest" was a card that simply looked as though nothing had begun. */}
          {parked || d.lifecycle === 'declined' ? <span className="dl-state">{stateOrStep(d)}</span> : null}
        </div>
        <p className="dl-headline">{d.title}</p>
        {moved
          ? <p className="dl-moved">{moved.line}{moved.source ? <span className="src">{moved.source}</span> : null}</p>
          : <p className="dl-moved">{d.lastHeardAt ? `Nothing new since ${formatDate(d.lastHeardAt)}.` : 'Nothing has been heard on this deal.'}</p>}
      </div>
      <div className="r">
        {d.nextFixedAt ? <span className="dl-plate">{formatDate(d.nextFixedAt)}</span> : null}
        <Track steps={steps} at={parked || d.lifecycle === 'declined' ? null : d.stageCode} />
        <div className="dl-standing">
          {/* Read, never assumed: stepWords says "No step open" only when the desk read came back
              with nothing accepted on the matter, and says whose the moment anybody accepts one. */}
          <span className="dl-who">{step.who}</span>
          {step.what ? <span className="dl-step">{step.what}{d.nextStep?.dueAt ? `, by ${formatDate(d.nextStep.dueAt)}` : ''}</span> : null}
          {/* `silent` is the API's judgement, not this page's (D68). */}
          {d.silent && d.silentDays !== null ? <span className="dl-silent">Silent for {d.silentDays} days</span> : null}
          {parked
            ? <ActionButton type="button" disabled={busy} onClick={() => void act(() => dealsApi.unpark(store, d.id), 'Back on the paper where it was.')}>Take it off the shelf</ActionButton>
            : d.lifecycle !== 'won' && d.lifecycle !== 'lost'
              ? <ActionButton type="button" aria-expanded={panel === 'park'} onClick={() => { setError(null); setPanel(panel === 'park' ? 'none' : 'park'); }}>Park until…</ActionButton>
              : null}
          <ActionButton reading type="button" aria-expanded={panel === 'why'} onClick={() => setPanel(panel === 'why' ? 'none' : 'why')}>Why is this here?</ActionButton>
        </div>
      </div>
      {/* Everything that is not the deal's name and where its paper stands runs the whole width under both,
          so the options sit on one line instead of wrapping beside the track. Empty, it draws nothing. */}
      <div className="b">
        {d.stateReason ? <div className="dl-quote">“{d.stateReason}”</div> : null}

        {/* The roll-out folded in (U154): drawn only when crm.offer holds rows for this deal. The
            whole list is the deal's own page, which "Every operator" opens. */}
        {d.rollout
          ? (
            <div className="dl-roll">
              <div className="dl-roll-h">
                <strong>Selling it</strong>
                <span>{rolloutWords(d.rollout)}</span>
                <a href={`/deals/${d.id}`} onClick={(e) => { e.preventDefault(); open(`/deals/${d.id}`); }}>Every operator</a>
              </div>
              <div className="dl-roll-rows" role="table" aria-label="Four of the operators">
                {d.rollout.rows.map((o) => (
                  <div className="dl-roll-row" role="row" key={o.id}>
                    <span className="op" role="cell">{o.operatorLabel}</span>
                    <span role="cell">{stateWords(o.state)}{o.stateJudged ? <span className="sub"> read by Cobrain</span> : null}</span>
                    <span className="sub" role="cell">{o.holderName ? o.holderName.split(' ')[0] : 'Nobody'}</span>
                  </div>
                ))}
              </div>
            </div>
          )
          : null}

        {/* U185: the cases Cobrain's own close leaves alone -- the notice came before the deal was opened here, or the
            company has more than one open deal -- are offered to a person, who closes it or says it is not this one.
            The pair is checked against what Cobrain offers this reader when pressed, never taken from the page. */}
        {d.signedCopies.map((c) => (
          <div className="dl-closed held" role="note" key={`${c.noticeSignalId}|${c.document}`}>
            <p>{heldCopyWords(c)}</p>
            <div className="dl-opts">
              <ActionButton type="button" className="dl-opt lead" disabled={busy}
                onClick={() => void act(() => dealsApi.closeOnCopy(store, d.id, c.noticeSignalId), `${d.title}: closed as signed, with the signed copy filed as its contract.`, true)}>
                Close it as signed
              </ActionButton>
              <ActionButton type="button" className="dl-opt" disabled={busy}
                onClick={() => void act(() => dealsApi.notThisDeal(store, d.id, c.noticeSignalId), `${d.title}: that signed copy is taken off it and will not be offered there again.`, true)}>
                Not this deal
              </ActionButton>
            </div>
          </div>
        ))}

        {/* The note that fades (U147): drawn for the holder alone, for a fortnight after the close,
            and gone the moment it is reopened. The API reads only the reader's own close. */}
        {d.closed
          ? (
            <div className="dl-closed" role="note">
              <p>{closedWords(d.closed)}</p>
              <div className="dl-opts">
                {d.closed.contractId
                  ? <ActionButton reading type="button" className="dl-opt" onClick={() => open(`/contracts/${encodeURIComponent(d.closed!.contractId!)}`)}>See the signed copy</ActionButton>
                  : null}
                <ActionButton type="button" className="dl-opt" disabled={busy}
                  onClick={() => void act(() => dealsApi.reopen(store, d.id), `Reopened: back at ${stepName(steps, d.closed!.fromStage)}.`)}>
                  Reopen it
                </ActionButton>
              </div>
            </div>
          )
          : null}

        {/* Cobrain's lead and one or two options: the reader's own newest triage item on this deal
            (D63). No item on the pass, no lead -- the card never makes one up (the brief, section 5). */}
        {d.lead
          ? (
            <div className="dl-opts">
              {d.lead.options.map((o, i) => (
                <Fragment key={o.key}>
                  <ActionButton type="button" className={i === 0 ? 'dl-opt lead' : 'dl-opt'} disabled={busy} onClick={() => choose(o)}>
                    {o.label}{o.note ? <span className="n">{o.note}</span> : null}
                  </ActionButton>
                  {o.kind === 'call' ? <ActionButton type="button" className="dl-opt" disabled={busy} onClick={() => void call(o, true)}>{VIDEO_CALL}</ActionButton> : null}
                </Fragment>
              ))}
            </div>
          )
          : null}
        {/* A deal nobody holds (U156) carries the three presses, wherever it is drawn as a card: in its own band, or in
            Yours when the reader is on it. A closed one is history and needs nobody. */}
        {d.holderPrincipalId === null && !closedDeal(d) ? <AllocateOpts d={d} me={me} giveTo={giveTo} allocate={allocate} /> : null}
        {said ? <p className="dl-said" role="status">{said}</p> : null}
        {error ? <p className="dl-err" role="alert">{error}</p> : null}

        {panel === 'park'
          ? (
            <div className="dl-panel">
              <div className="dl-opts">
                {presets.map((p) => (
                  <ActionButton key={p.key} type="button" className={`dl-opt${until === p.until.toISOString().slice(0, 10) ? ' on' : ''}`} aria-pressed={until === p.until.toISOString().slice(0, 10)}
                    onClick={() => setUntil(p.until.toISOString().slice(0, 10))}>{p.label}</ActionButton>
                ))}
                <label className="dl-date">Until <ActionInput type="date" value={until} min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)} /></label>
              </div>
              <label className="dl-reason">Why it waits
                <ActionInput type="text" value={reason} maxLength={300} placeholder="They come back after the budget round" onChange={(e) => setReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') park(); }} />
              </label>
              <div className="dl-opts">
                <ActionButton type="button" className="dl-opt lead" disabled={busy || !until} onClick={park}>Park it</ActionButton>
                <ActionButton type="button" className="dl-opt" onClick={() => setPanel('none')}>Keep it open</ActionButton>
              </div>
            </div>
          )
          : null}
        {panel === 'why'
          ? (
            <div className="dl-panel dl-why">
              {d.lead ? <p><strong>{placeWords(d.lead)}.</strong> {d.lead.reason ?? ''}</p> : <p>Cobrain has nothing on your list about this deal.</p>}
              <p>{yoursWords(d, me)}</p>
              {itemPage ? <a href={itemPage} onClick={(e) => { e.preventDefault(); open(itemPage); }}>Open the item</a> : null}
            </div>
          )
          : null}
      </div>
    </article>
  );
}

/** What the reader is told when a press on a heard row did not go through. The route's words, never the store's. */
export function refusedWords(e: unknown): string {
  return `That did not go through: ${e instanceof Error ? e.message.replace(/\.$/, '') : String(e)}. Nothing was changed.`;
}

type HeardPanel = 'none' | 'company' | 'no' | 'give';

/**
 * One thing Cobrain heard that might be a deal (U146). The reader's own leads with "Accept, it's
 * mine", then "Not a deal", which puts it away and is what Cobrain learns from.
 *
 * A colleague's row carries "Take it off <name>": it becomes the reader's to accept. The brief's lead there,
 * "Leave it with <name>", is NOT drawn: pressing it would change nothing, and a button that does nothing is worse
 * than none (U152). The reader's own row adds "Give it to someone else", a person from the store's team.
 */
export function HeardCard({ h, store, me, team, open, onAnswered }: { h: Heard; store: string; me: string; team: Desk['team']; open: (p: string) => void; onAnswered: (words: string) => void }) {
  const owner = h.ownerName ? h.ownerName.split(' ')[0] : null;
  const others = team.filter((t) => t.principalId !== me);
  const [panel, setPanel] = useState<HeardPanel>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const [find, setFind] = useState('');
  const [found, setFound] = useState<Array<{ id: string; name: string }>>([]);

  // The company picker reads the store's own company list as the reader, by name; it never offers a
  // company the reader could not already open.
  useEffect(() => {
    if (panel !== 'company' || find.trim().length < 2) { setFound([]); return; }
    let live = true;
    const t = window.setTimeout(() => {
      records.companies(store, { q: find.trim() })
        .then((r) => { if (live) setFound(r.companies.slice(0, 6).map((c) => ({ id: c.id, name: c.name }))); })
        .catch(() => { if (live) setFound([]); });
    }, 250);
    return () => { live = false; window.clearTimeout(t); };
  }, [panel, find, store]);

  const act = async (fn: () => Promise<unknown>, words: string) => {
    setBusy(true); setError(null);
    try { await fn(); onAnswered(words); }
    catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(refusedWords(e));
    } finally { setBusy(false); }
  };

  const accept = (org: { id: string; name: string } | null) => {
    const name = org?.name ?? h.organisationName;
    void act(() => dealsApi.accept(store, h.id, org?.id ?? null), name ? `Accepted: the deal with ${name} is in Yours, at Talking.` : 'Accepted: it is in Yours, at Talking.');
  };

  return (
    <article className="dl-card">
      <div className="dl-meta">
        {sideWord(h.side) ? <span className="dl-side">{sideWord(h.side)}</span> : null}
        {h.organisationName
          ? (h.organisationId
            ? <ActionButton reading type="button" className="dotted" onClick={() => open(`/companies/${h.organisationId}`)}>{h.organisationName}</ActionButton>
            : <span>{h.organisationName}</span>)
          : <span>No company named</span>}
        <span>Heard in {heardInWords(h.heardIn)}, {formatDate(h.heardAt)}</span>
        <span className="dl-chip">{h.mine ? 'Yours to accept' : owner ? `${owner}’s to accept` : 'For anyone to take'}</span>
      </div>
      <p className="dl-headline">{h.headline}</p>
      {/* No quote is drawn when the rails did not give one back: an empty quote box would say
          Cobrain heard nothing, when in fact the reader may not read what it heard. */}
      {h.quote ? <div className="dl-quote">“{h.quote}”</div> : null}
      {h.mine
        ? (
          <div className="dl-opts">
            {/* A deal always has a company. When Cobrain named none, the lead asks for one first
                rather than being refused by the store. */}
            <ActionButton type="button" className="dl-opt lead" disabled={busy}
              onClick={() => { setError(null); if (h.organisationId) accept(null); else setPanel(panel === 'company' ? 'none' : 'company'); }}>
              Accept, it’s mine
            </ActionButton>
            <ActionButton type="button" className="dl-opt" disabled={busy} aria-expanded={panel === 'no'}
              onClick={() => { setError(null); setPanel(panel === 'no' ? 'none' : 'no'); }}>
              Not a deal<span className="n">Cobrain learns from it</span>
            </ActionButton>
          </div>
        )
        : (
          <div className="dl-opts">
            <ActionButton type="button" className="dl-opt" disabled={busy}
              onClick={() => void act(() => dealsApi.pass(store, h.id, me), `${h.headline}: yours to accept now${owner ? `, taken off ${owner}` : ''}.`)}>
              {owner ? `Take it off ${owner}` : 'Take it'}<span className="n">It becomes yours to accept</span>
            </ActionButton>
          </div>
        )}
      {h.mine && others.length > 0
        ? (
          <div className="dl-standing">
            <ActionButton type="button" aria-expanded={panel === 'give'} onClick={() => { setError(null); setPanel(panel === 'give' ? 'none' : 'give'); }}>Give it to someone else</ActionButton>
          </div>
        )
        : null}
      {error ? <p className="dl-err" role="alert">{error}</p> : null}
      {panel === 'company'
        ? (
          <div className="dl-panel">
            <label className="dl-reason">Which company is it with?
              <ActionInput type="search" value={find} placeholder="Start typing a company’s name" autoFocus onChange={(e) => setFind(e.target.value)} />
            </label>
            {found.length > 0
              ? (
                <div className="dl-opts">
                  {found.map((c) => <ActionButton key={c.id} type="button" className="dl-opt" disabled={busy} onClick={() => accept(c)}>{c.name}</ActionButton>)}
                </div>
              )
              : find.trim().length >= 2 ? <p className="dl-said">No company by that name.</p> : null}
          </div>
        )
        : null}
      {panel === 'give'
        ? (
          <div className="dl-panel">
            <p className="dl-reason">Whose is it to accept?</p>
            <div className="dl-opts">
              {others.map((t) => (
                <ActionButton key={t.principalId} type="button" className="dl-opt" disabled={busy}
                  onClick={() => void act(() => dealsApi.pass(store, h.id, t.principalId), `${h.headline}: given to ${t.name.split(' ')[0]} to accept.`)}>
                  {t.name}
                </ActionButton>
              ))}
              <ActionButton type="button" className="dl-opt" onClick={() => setPanel('none')}>Keep it</ActionButton>
            </div>
          </div>
        )
        : null}
      {panel === 'no'
        ? (
          <div className="dl-panel">
            <label className="dl-reason">What was it, if you want to say
              <ActionInput type="text" value={why} maxLength={500} placeholder="A supplier asking for a quote"
                onChange={(e) => setWhy(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void act(() => dealsApi.notADeal(store, h.id, why.trim()), 'Put away. Cobrain will learn from it.'); }} />
            </label>
            <div className="dl-opts">
              <ActionButton type="button" className="dl-opt lead" disabled={busy} onClick={() => void act(() => dealsApi.notADeal(store, h.id, why.trim()), 'Put away. Cobrain will learn from it.')}>Put it away</ActionButton>
              <ActionButton type="button" className="dl-opt" onClick={() => setPanel('none')}>Keep it</ActionButton>
            </div>
          </div>
        )
        : null}
    </article>
  );
}

function TeamRows({ deals, me, open, alloc }: { deals: DeskDeal[]; me: string; open: (p: string) => void; alloc?: { giveTo: Person[]; allocate: AllocateFn } }) {
  const [all, setAll] = useState(false);
  // Every deal in the band may already be drawn as a card above, and a table head over no rows is noise.
  if (deals.length === 0) return null;
  const shown = all ? deals : deals.slice(0, 12);
  return (
    <div className={alloc ? 'dl-rows alloc' : 'dl-rows'} role="table">
      {/* Every cell carries its role. A role="row" whose children are plain spans announces as a
          table with no cells in it, which is worse to listen to than no roles at all. */}
      <div className="dl-row h" role="row">
        <span role="columnheader">Deal</span><span role="columnheader">Held by</span>
        <span role="columnheader">Paper</span><span role="columnheader">What moved</span>
        <span role="columnheader">Next fixed time</span>
        {alloc ? <span role="columnheader"><span className="sr-only">What to do with it</span></span> : null}
      </div>
      {shown.map((d) => {
        const moved = movedWords(d);
        return (
          <div className="dl-row" role="row" key={d.id}>
            <span role="cell"><a href={`/deals/${d.id}`} onClick={(e) => { e.preventDefault(); open(`/deals/${d.id}`); }}>{d.title}</a>
              {d.organisationName !== null ? <><br /><span className="sub"><CompanyName id={d.organisationId} name={d.organisationName} go={open} /></span></> : null}</span>
            {/* Who HOLDS it, which is not the same question as `mine`: `mine` is true for a deal
                the reader is only ON (U145), and this column would then have said "You" of a deal
                that is Will's. */}
            <span className="c2" role="cell">{d.holderPrincipalId === me ? 'You' : d.holderName ?? 'Nobody'}</span>
            {/* A deal parked or declined shows that state and not a step: U153 keeps the two apart. */}
            <span className="c3" role="cell">{stateOrStep(d)}</span>
            <span className="c4" role="cell">
              {moved ? moved.line : d.lastHeardAt ? `Nothing new since ${formatDate(d.lastHeardAt)}` : 'Nothing heard'}
              {d.silent && d.silentDays !== null ? <> <span className="dl-silent">Silent for {d.silentDays} days</span></> : null}
            </span>
            <span role="cell">{d.nextFixedAt ? <span className="dl-plate">{formatDate(d.nextFixedAt)}</span> : null}</span>
            {alloc ? <AllocateOpts d={d} me={me} giveTo={alloc.giveTo} allocate={alloc.allocate} row /> : null}
          </div>
        );
      })}
      {deals.length > shown.length
        ? <div className="dl-more"><ActionButton reading type="button" onClick={() => setAll(true)}>{deals.length - shown.length} more deals</ActionButton></div>
        : null}
    </div>
  );
}

export function DealsPage({ store, open }: PageProps) {
  const [loaded, reload] = useLoad(() => dealsApi.desk(store.tenantId), [store.tenantId]);
  const [q, setQ] = useState('');
  // The find reads the store as the reader (brief section 5): deals by title or alias, companies by name or alias, and
  // people, never the deals already on this page alone. `found` carries the query it answers, so a slow answer to an
  // older query never replaces a newer one.
  const [found, setFound] = useState<(Found & { q: string }) | null>(null);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound(null); return; }
    let live = true;
    const t = window.setTimeout(() => {
      dealsApi.find(store.tenantId, term).then((r) => { if (live) setFound(r); }).catch(() => { if (live) setFound(null); });
    }, 200);
    return () => { live = false; window.clearTimeout(t); };
  }, [q, store.tenantId]);
  // What the last answer to a heard row did. It lives on the page, not the card, because the card
  // leaves the band on the next read -- an answered proposal is no longer waiting.
  const [heardSaid, setHeardSaid] = useState<string | null>(null);
  // What a press on a deal card did, when the press moved the card out of sight (DealCard's `leaves`).
  const [pageSaid, setPageSaid] = useState<string | null>(null);
  // The strip at the foot of the page after a take, a give or a remove (U156), with Undo (D62): the press has already
  // been written, so Undo is a second press that puts the deal back, not a hold that was never sent.
  const [strip, setStrip] = useState<{ words: string; undo: { dealId: string; actionId: string; title: string } | null } | null>(null);
  const stripTimer = useRef(0);
  const holdStrip = () => { window.clearTimeout(stripTimer.current); stripTimer.current = window.setTimeout(() => setStrip(null), ALLOCATE_STRIP_MS); };
  const showStrip = (s: NonNullable<typeof strip>) => { setStrip(s); holdStrip(); };
  useEffect(() => () => window.clearTimeout(stripTimer.current), []);
  // After a press the desk is read again QUIETLY: useLoad's reload blanks the page to "Loading", which
  // unmounts every card and takes the line saying what was just done with it. The fresh read replaces
  // the data in place instead, and a failed one leaves the last good read standing.
  const [fresh, setFresh] = useState<{ tenant: string; data: Desk } | null>(null);
  const refresh = () => { void dealsApi.desk(store.tenantId).then((data) => setFresh({ tenant: store.tenantId, data })).catch(() => undefined); };
  const state: typeof loaded = loaded.kind === 'ready' && fresh && fresh.tenant === store.tenantId ? { kind: 'ready', data: fresh.data } : loaded;

  const allocate: AllocateFn = async (d, kind, to) => {
    try {
      const r = kind === 'take' ? await dealsApi.take(store.tenantId, d.id)
        : kind === 'give' ? await dealsApi.give(store.tenantId, d.id, to!.principalId)
          : await dealsApi.remove(store.tenantId, d.id);
      showStrip({ words: allocatedWords(d.title, kind, to?.name ?? null), undo: { dealId: d.id, actionId: r.actionId, title: d.title } });
      refresh();
      return null;
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return null; }
      return refusedWords(e);
    }
  };
  const undoAllocation = async () => {
    const u = strip?.undo;
    if (!u) return;
    window.clearTimeout(stripTimer.current);
    setStrip({ words: `Putting ${u.title} back…`, undo: null });
    try {
      await dealsApi.undoAllocation(store.tenantId, u.dealId, u.actionId);
      showStrip({ words: undoneWords(u.title), undo: null });
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      showStrip({ words: `Undo did not go through: ${e instanceof Error ? e.message.replace(/\.$/, '') : String(e)}.`, undo: null });
    }
    refresh();
  };

  const bands = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const { deals } = state.data;
    // U145, "we hunt as a team": YOURS is what the reader holds OR is on, and `mine` is the API's
    // answer to exactly that question (owner, or a row in crm.deal_member). Banding on the holder
    // alone buried a deal the reader is on in the team's one-line rows, which is the burial
    // own-first exists to prevent. `holderPrincipalId` stays the answer to the other question --
    // who holds it -- and that is what the "Held by" column reads.
    // A CLOSED deal is history, not work (module 04 D263 keeps the 84 signed HubSpot deals under
    // their holder for exactly that reason), so it leaves the working bands for a folded "Signed and
    // closed" band at the foot -- UNLESS the reader's own list still has something on it, a renewal's
    // notice cut-off for instance, in which case it is work and stays where the work is. And a
    // RIGHTS deal is signed the day the rights are acquired, which is the day selling them begins:
    // a signed deal with a roll-out is the deal most in motion on the page (Premier Padel and
    // Uruguay went into the folded band on the evening they were seeded, their operators unseen).
    const closed = foldedAway;
    const mine = deals.filter((d) => d.mine && !closed(d));
    // U156: a deal nobody holds sits in its own band, unless the reader is on it, in which case
    // own-first wins -- it is not waiting for somebody to take it if somebody is already on it.
    const unallocated = deals.filter((d) => !d.mine && d.holderPrincipalId === null && !closed(d));
    const team = deals.filter((d) => !d.mine && d.holderPrincipalId !== null && !closed(d));
    const done = deals.filter(closed);
    return { mine, team, unallocated, done };
  }, [state]);

  if (state.kind === 'loading') return <div className="deals"><Loading what="the deals" /></div>;
  if (state.kind === 'error') {
    return (
      <div className="deals">
        <div className="dl-says">
          <div className="dl-err">Deals did not load. Nothing was changed.</div>
          <div className="dl-opts"><ActionButton reading type="button" className="dl-opt" onClick={reload}>Try again</ActionButton></div>
        </div>
      </div>
    );
  }

  const { stages, deals, heard, more } = state.data;
  const steps = stepsOf(stages);
  const { mine, team, unallocated, done } = bands!;
  const moves = byNextMove(mine);
  // Older API answers have no list; the picker then says there is nobody to give it to rather than failing.
  const giveTo = state.data.giveTo ?? [];


  // A deal outside Yours whose card the reader's OWN list still has something on -- "you owe Conor
  // this counter-offer on the Kaizen deal" on a deal nobody holds yet -- is drawn as a card, lead and
  // all, at the head of its band. Folded into a one-line row its lead would be invisible, and the
  // lead is the reason the desk exists. It stays in the band the store puts it in (U145, U156):
  // having an item about a deal does not make the reader its holder.
  const cards = (ds: DeskDeal[]) => ds.filter(asCard).map((d) => (
    <DealCard key={d.id} d={d} steps={steps} me={state.data.me} store={store.tenantId} open={open} onChanged={refresh} onSaid={setPageSaid} giveTo={giveTo} allocate={allocate} />
  ));

  return (
    <div className="deals">
      <div className="dl-top">
        <h1 className="dl-title">Deals</h1>
        <div className="dl-find">
          <ActionInput reading
            type="search" value={q} placeholder="Find a deal, a company or a person"
            aria-label="Find a deal, a company or a person"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setQ(''); }}
          />
        </div>
      </div>
      {pageSaid ? <p className="dl-said page" role="status">{pageSaid}</p> : null}
      {q.trim().length >= 2
        ? (
          <div className="dl-found" role="region" aria-label="Found">
            {!found || found.q !== q.trim()
              ? <p className="dl-says gist">Looking…</p>
              : found.deals.length + found.companies.length + found.people.length === 0
                ? <p className="dl-says gist">Nothing on record matches that.</p>
                : (
                  <>
                    {found.deals.length > 0 ? <p className="h">Deals</p> : null}
                    {found.deals.map((d) => (
                      <ActionButton reading type="button" key={`d${d.id}`} onClick={() => { setQ(''); open(`/deals/${d.id}`); }}>
                        <span>{d.title}</span>{foundDealWhat(d) ? <span className="what">{foundDealWhat(d)}</span> : null}
                      </ActionButton>
                    ))}
                    {found.companies.length > 0 ? <p className="h">Companies</p> : null}
                    {found.companies.map((c) => (
                      <ActionButton reading type="button" key={`c${c.id}`} onClick={() => { setQ(''); open(`/companies/${c.id}`); }}>
                        <span>{c.name}</span>
                      </ActionButton>
                    ))}
                    {found.people.length > 0 ? <p className="h">People</p> : null}
                    {found.people.map((p) => (
                      <ActionButton reading type="button" key={`p${p.id}`} onClick={() => { setQ(''); open(`/people/${p.id}`); }}>
                        <span>{p.name}</span>{p.organisationName ? <span className="what">{p.organisationName}</span> : null}
                      </ActionButton>
                    ))}
                  </>
                )}
          </div>
        )
        : null}

      {/* The show strip (U149, U158): track `show`'s component, drawn only while a show with a booked
          meeting is ahead or running, and drawn not at all when it cannot be read. */}
      <ShowStrip store={store.tenantId} open={open} />

      {/* "Cobrain heard these" is drawn while the deal-proposals pass is switched on (`heardOn`, the pass's own
          setting), and then says so in words when nothing is waiting: a band that vanished when the pass heard
          nothing would read as a quiet day rather than a pass that read everything and found nothing. While the
          pass is off it is drawn only if a proposal written earlier still waits. */}
      {heard.length > 0 || heardSaid || state.data.heardOn
        ? (
          <>
            <div className="dl-band"><h2>Cobrain heard these</h2><span className="c">{heard.length} waiting</span></div>
            {heardSaid ? <p className="dl-said" role="status">{heardSaid}</p> : null}
            {heard.length === 0 && !heardSaid
              ? <div className="dl-says"><p className="gist">Nothing waiting. Cobrain has heard nothing new that looks like a deal.</p></div>
              : null}
            {heard.map((h) => <HeardCard key={h.id} h={h} store={store.tenantId} me={state.data.me} team={state.data.team ?? []} open={open} onAnswered={(w) => { setHeardSaid(w); refresh(); }} />)}
          </>
        )
        : null}

      <div className="dl-band"><h2>Yours</h2><span className="c">{countWords(mine.length, more)}</span></div>
      {mine.length === 0
        ? <div className="dl-says"><p className="gist">You hold no deals and are on none. The team’s are below.</p></div>
        : MOVE_GROUPS.map((g) => {
          // Every group is drawn, an empty one as its heading and one line of what was read (U152): today almost every
          // deal is in "No step open" because no step on any deal has been accepted, and that is the finding (B1569).
          const ds = moves[g.key];
          return (
            <Fragment key={g.key}>
              <div className="dl-group"><h3>{g.head}</h3><span className="c">{ds.length === 0 ? g.empty : countWords(ds.length, more)}</span></div>
              {ds.map((d) => <DealCard key={d.id} d={d} steps={steps} me={state.data.me} store={store.tenantId} open={open} onChanged={refresh} onSaid={setPageSaid} giveTo={giveTo} allocate={allocate} />)}
            </Fragment>
          );
        })}

      {unallocated.length > 0
        ? (
          <UnallocatedBand words={unallocatedWords(unallocated.length, more)}>
            {cards(unallocated)}
            <TeamRows deals={unallocated.filter((d) => !asCard(d))} me={state.data.me} open={open} alloc={{ giveTo, allocate }} />
          </UnallocatedBand>
        )
        : null}

      {/* The team's deals as full cards (the Deals contract, round 2: "Team's deals as full cards"), every one of them,
          lead or none, in the read's own order. */}
      <div className="dl-band"><h2>The team’s</h2><span className="c">{countWords(team.length, more)}</span></div>
      {team.length === 0
        ? <div className="dl-says"><p className="gist">No deal on this store is held by anyone else.</p></div>
        : team.map((d) => <DealCard key={d.id} d={d} steps={steps} me={state.data.me} store={store.tenantId} open={open} onChanged={refresh} onSaid={setPageSaid} giveTo={giveTo} allocate={allocate} />)}

      {done.length > 0 ? <ClosedBand deals={done} me={state.data.me} open={open} /> : null}

      {/* The strip after a take, a give or a remove: triage's (U34), the same place and the same hold. */}
      <div className={strip ? 'undo on' : 'undo'} role="status" aria-live="polite"
        onMouseEnter={() => window.clearTimeout(stripTimer.current)} onMouseLeave={() => { if (strip) holdStrip(); }}
        onFocus={() => window.clearTimeout(stripTimer.current)} onBlur={() => { if (strip) holdStrip(); }}>
        {strip
          ? <><span className="undo-words"><span>{strip.words}</span></span>{strip.undo ? <ActionButton type="button" onClick={() => void undoAllocation()}>Undo</ActionButton> : null}</>
          : null}
      </div>
    </div>
  );
}

/**
 * Whether a deal goes to the folded "Signed and closed" band: closed, nothing on the reader's list about
 * it, no roll-out being sold, and no close by Cobrain still being told to its holder -- a deal Cobrain
 * closed without a press stays where the holder will see the note and can undo it (U147).
 */
export function foldedAway(d: DeskDeal): boolean {
  return closedDeal(d) && d.lead === null && d.rollout === null && d.closed === null;
}

/** A step's own name from the store, or its code when the store no longer defines it. */
export function stepName(steps: StageStep[], code: string): string {
  return steps.find((x) => x.code === code)?.name ?? code;
}

/**
 * Whether a deal outside Yours is drawn as a card rather than a row: something on it asks for a press -- an item on
 * the reader's own list, or a signed copy the store already holds (U185). In a one-line row either would be invisible.
 */
export function asCard(d: DeskDeal): boolean {
  return d.lead !== null || d.signedCopies.length > 0;
}

/** What a held signed copy says, as fact: the document and the day the e-signature service said it was complete. */
export function heldCopyWords(c: HeldCopy): string {
  return `A signed copy of “${c.document}” is already held: the e-signature service said on ${formatDate(c.signedOn)} that everyone had signed.`;
}

/** The note to the holder when Cobrain closed the deal itself (U147, module 04 D270). */
export function closedWords(c: Closed): string {
  const on = formatDate(c.noticeAt ?? c.closedAt);
  const doc = c.document ? ` for “${c.document}”` : '';
  return `Cobrain marked this signed: the e-signature service said on ${on} that everyone had signed${doc}.`;
}

/** Signed or lost: the lifecycle the store gives the step, so the page keeps no list of its own. */
export function closedDeal(d: DeskDeal): boolean {
  return d.lifecycle === 'won' || d.lifecycle === 'lost';
}

/**
 * Not allocated, folded to one line between Yours and the team's deals: the live count and one press that opens the
 * band as it was drawn before, cards for a deal with a lead or a signed copy and then the rows, each with Take it, Give
 * it to someone and Remove. The same fold as Signed and closed, so the desk has one way of folding. Open, it stays open
 * across a press and the quiet re-read that follows, so a take from the rows leaves the reader where they were.
 */
function UnallocatedBand({ words, children }: { words: string; children: ReactNode }) {
  const [shown, setShown] = useState(false);
  return (
    <>
      <div className="dl-band">
        <h2>{words}</h2>
        <ActionButton reading type="button" className="dl-fold" aria-expanded={shown} onClick={() => setShown(!shown)}>{shown ? 'Fold them away' : 'Show them'}</ActionButton>
      </div>
      {shown ? children : null}
    </>
  );
}

/** The closed deals, folded: a count and one press to see them, because they are what was done, not what to do. */
function ClosedBand({ deals, me, open }: { deals: DeskDeal[]; me: string; open: (p: string) => void }) {
  const [shown, setShown] = useState(false);
  return (
    <>
      <div className="dl-band">
        <h2>Signed and closed</h2><span className="c">{countWords(deals.length, false)}</span>
        <ActionButton reading type="button" className="dl-fold" aria-expanded={shown} onClick={() => setShown(!shown)}>{shown ? 'Fold them away' : 'Show them'}</ActionButton>
      </div>
      {shown ? <TeamRows deals={deals} me={me} open={open} /> : null}
    </>
  );
}
